import { useEffect, useMemo, useState } from "react";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Select,
    SelectItem,
    NumberInput,
    Alert,
    Chip,
} from "@heroui/react";
import {
    LuChevronsRight,
    LuArrowLeft,
    LuChevronDown,
    LuChevronRight,
} from "react-icons/lu";
import { ThemedText } from "@/components/ui";
import { useFetchData } from "@/hooks";
import { formatThousands, showToast } from "@/utils";
import axios from "axios";
import { BASE_API } from "@/constants";
import { today, getLocalTimeZone } from "@internationalized/date";

const ConfirmFormulaProductionModal = ({
    isOpen,
    onClose,
    onBack,
    formula,
    onSuccess,
}) => {
    const [destinationWarehouseId, setDestinationWarehouseId] = useState("");
    const [stockCheckData, setStockCheckData] = useState(null);
    const [isCheckingStock, setIsCheckingStock] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Allocations: { [material_id]: { [warehouse_id]: qty } }
    const [allocations, setAllocations] = useState({});

    // Expanded rows
    const [expandedItems, setExpandedItems] = useState(new Set());

    // Fetch warehouses for destination
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");

    // Check Stock on open
    useEffect(() => {
        let active = true;

        if (isOpen && formula) {
            const checkStock = async () => {
                setIsCheckingStock(true);
                try {
                    const res = await axios.post(
                        `${BASE_API}/material-formulas/check-production-stock`,
                        {
                            formula_id: formula.id,
                            quantity: formula.quantity,
                        },
                    );
                    if (active) {
                        setStockCheckData(res.data.data);
                        // Initialize allocations with empty values
                        const initAllocations = {};
                        res.data.data.items.forEach((item) => {
                            initAllocations[item.material_id] = {};
                            item.warehouses.forEach((wh) => {
                                initAllocations[item.material_id][
                                    wh.warehouse_id
                                ] = null;
                            });
                        });
                        setAllocations(initAllocations);
                    }
                } catch (e) {
                    console.error(e);
                } finally {
                    if (active) setIsCheckingStock(false);
                }
            };
            checkStock();
            return () => {
                active = false;
            };
        }
    }, [isOpen, formula]);

    // Reset on close
    useEffect(() => {
        if (!isOpen) {
            const timer = setTimeout(() => {
                setDestinationWarehouseId("");
                setStockCheckData(null);
                setAllocations({});
                setExpandedItems(new Set());
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Toggle expand - only one at a time
    const toggleExpand = (materialId) => {
        setExpandedItems((prev) => {
            // If already expanded, close it
            if (prev.has(materialId)) {
                return new Set();
            }
            // Otherwise, open only this one
            return new Set([materialId]);
        });
    };

    // Update allocation with max clamp
    const updateAllocation = (materialId, warehouseId, value, maxValue) => {
        const clampedValue = Math.min(
            Math.max(0, Number(value) || 0),
            maxValue,
        );
        setAllocations((prev) => ({
            ...prev,
            [materialId]: {
                ...prev[materialId],
                [warehouseId]: clampedValue,
            },
        }));
    };

    // Validation
    const validationResults = useMemo(() => {
        if (!stockCheckData) return { isValid: false, items: [] };

        const items = stockCheckData.items.map((item) => {
            const materialAllocs = allocations[item.material_id] || {};
            const totalAllocated = Object.values(materialAllocs).reduce(
                (sum, qty) => sum + (qty || 0),
                0,
            );
            const required = item.required_qty;
            const isExact = totalAllocated === required;

            return {
                ...item,
                totalAllocated,
                isExact,
            };
        });

        const allExact = items.every((item) => item.isExact);

        return {
            isValid: allExact && destinationWarehouseId,
            items,
        };
    }, [stockCheckData, allocations, destinationWarehouseId]);

    // Handle Confirm
    const handleConfirm = async () => {
        const allocatedItems = [];
        validationResults.items.forEach((item) => {
            const materialAllocs = allocations[item.material_id] || {};
            Object.entries(materialAllocs).forEach(([whId, qty]) => {
                if (qty > 0) {
                    allocatedItems.push({
                        material_id: item.material_id,
                        material_unit_id: item.material_unit_id,
                        warehouse_id: Number(whId),
                        quantity: qty,
                    });
                }
            });
        });

        const payload = {
            production_date: today(getLocalTimeZone()).toString(),
            material_id: formula.material_id,
            material_unit_id: formula.material_unit_id,
            quantity: formula.quantity,
            warehouse_id: destinationWarehouseId,
            material_formula_id: formula.id,
            items: allocatedItems,
        };

        setIsSubmitting(true);
        try {
            await axios.post(`${BASE_API}/material-productions/store`, payload);
            showToast("success", "Berhasil", "Produksi berhasil dibuat!");
            if (onSuccess) onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            const message =
                error.response?.data?.message || "Gagal membuat produksi";
            showToast("error", "Gagal", message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!formula) return null;

    const productionDate = today(getLocalTimeZone());
    const formattedDate = productionDate.toDate().toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="3xl"
            scrollBehavior="inside"
        >
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex items-center gap-2">
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                onPress={onBack}
                            >
                                <LuArrowLeft size={18} />
                            </Button>
                            <ThemedText size="md" className="font-medium">
                                Konfirmasi Produksi
                            </ThemedText>
                        </ModalHeader>

                        <ModalBody className="gap-6">
                            {/* Info Section - Key Value Style */}
                            <div className="bg-default-50 rounded-lg p-3">
                                <div className="grid grid-cols-2 gap-y-2">
                                    <ThemedText size="sm" variant="secondary">
                                        Tanggal
                                    </ThemedText>
                                    <ThemedText
                                        size="sm"
                                        className="font-medium"
                                    >
                                        {formattedDate}
                                    </ThemedText>

                                    <ThemedText size="sm" variant="secondary">
                                        Output Produk
                                    </ThemedText>
                                    <ThemedText
                                        size="sm"
                                        className="font-medium"
                                    >
                                        {formula.material?.name || formula.name}
                                    </ThemedText>

                                    <ThemedText size="sm" variant="secondary">
                                        Jumlah Output
                                    </ThemedText>
                                    <ThemedText
                                        size="sm"
                                        className="font-medium"
                                    >
                                        {formatThousands(formula.quantity)}{" "}
                                        {formula.unit?.name || ""}
                                    </ThemedText>

                                    <ThemedText size="sm" variant="secondary">
                                        Gudang Tujuan
                                    </ThemedText>
                                    <div>
                                        <Select
                                            aria-label="Gudang Tujuan"
                                            placeholder="Pilih gudang tujuan"
                                            selectedKeys={
                                                destinationWarehouseId
                                                    ? [destinationWarehouseId]
                                                    : []
                                            }
                                            onSelectionChange={(keys) => {
                                                const val = Array.from(keys)[0];
                                                setDestinationWarehouseId(
                                                    val || "",
                                                );
                                            }}
                                            isLoading={isLoadingWarehouses}
                                            size="sm"
                                            variant="flat"
                                        >
                                            {(warehouses || []).map((wh) => (
                                                <SelectItem
                                                    key={String(wh.value)}
                                                >
                                                    {wh.title}
                                                </SelectItem>
                                            ))}
                                        </Select>
                                    </div>
                                </div>
                            </div>

                            {/* Bahan Baku Table */}
                            <div>
                                <ThemedText
                                    size="sm"
                                    className="mb-3 font-medium"
                                >
                                    Alokasi Bahan Baku
                                </ThemedText>

                                {isCheckingStock ? (
                                    <div className="flex justify-center py-8">
                                        <Spinner size="md" />
                                    </div>
                                ) : validationResults.items.length > 0 ? (
                                    <div className="border-divider overflow-hidden rounded-lg border">
                                        {/* Table Header */}
                                        <div className="bg-default-100 grid grid-cols-12 gap-2 px-4 py-2">
                                            <div className="col-span-4">
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                    className="font-medium"
                                                >
                                                    Bahan Baku
                                                </ThemedText>
                                            </div>
                                            <div className="col-span-2">
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                    className="font-medium"
                                                >
                                                    Kebutuhan
                                                </ThemedText>
                                            </div>
                                            <div className="col-span-3">
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                    className="font-medium"
                                                >
                                                    Terisi
                                                </ThemedText>
                                            </div>
                                            <div className="col-span-3">
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                    className="font-medium"
                                                >
                                                    Status
                                                </ThemedText>
                                            </div>
                                        </div>

                                        {/* Table Body */}
                                        {validationResults.items.map((item) => {
                                            const isExpanded =
                                                expandedItems.has(
                                                    item.material_id,
                                                );

                                            return (
                                                <div key={item.material_id}>
                                                    {/* Main Row */}
                                                    <div
                                                        className="border-divider hover:bg-default-50 grid cursor-pointer grid-cols-12 items-center gap-2 border-t px-4 py-3 transition-colors"
                                                        onClick={() =>
                                                            toggleExpand(
                                                                item.material_id,
                                                            )
                                                        }
                                                    >
                                                        <div className="col-span-4 flex items-center gap-2">
                                                            {isExpanded ? (
                                                                <LuChevronDown
                                                                    size={14}
                                                                    className="text-default-400"
                                                                />
                                                            ) : (
                                                                <LuChevronRight
                                                                    size={14}
                                                                    className="text-default-400"
                                                                />
                                                            )}
                                                            <ThemedText
                                                                size="sm"
                                                                className="font-medium"
                                                            >
                                                                {
                                                                    item.material_name
                                                                }
                                                            </ThemedText>
                                                        </div>
                                                        <div className="col-span-2">
                                                            <ThemedText size="sm">
                                                                {formatThousands(
                                                                    item.required_qty,
                                                                )}{" "}
                                                                {item.unit_name}
                                                            </ThemedText>
                                                        </div>
                                                        <div className="col-span-3">
                                                            <ThemedText
                                                                size="sm"
                                                                className={
                                                                    item.isExact
                                                                        ? "text-success"
                                                                        : "text-warning"
                                                                }
                                                            >
                                                                {formatThousands(
                                                                    item.totalAllocated,
                                                                )}
                                                                /
                                                                {formatThousands(
                                                                    item.required_qty,
                                                                )}
                                                            </ThemedText>
                                                        </div>
                                                        <div className="col-span-3">
                                                            {item.isExact ? (
                                                                <Chip
                                                                    size="sm"
                                                                    color="success"
                                                                    variant="flat"
                                                                >
                                                                    Lengkap
                                                                </Chip>
                                                            ) : (
                                                                <Chip
                                                                    size="sm"
                                                                    color="warning"
                                                                    variant="flat"
                                                                >
                                                                    Belum
                                                                    Lengkap
                                                                </Chip>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Expanded Warehouse Rows */}
                                                    {isExpanded && (
                                                        <div className="bg-default-50 border-divider border-t">
                                                            {/* Sub Header */}
                                                            <div className="text-default-500 grid grid-cols-12 gap-2 px-4 py-1.5 font-medium">
                                                                <div className="col-span-4 pl-6">
                                                                    <ThemedText
                                                                        size="xs"
                                                                        variant="secondary"
                                                                        className="font-medium"
                                                                    >
                                                                        Gudang
                                                                    </ThemedText>
                                                                </div>
                                                                <div className="col-span-2">
                                                                    <ThemedText
                                                                        size="xs"
                                                                        variant="secondary"
                                                                        className="font-medium"
                                                                    >
                                                                        Stok
                                                                        Tersedia
                                                                    </ThemedText>
                                                                </div>
                                                                <div className="col-span-3">
                                                                    <ThemedText
                                                                        size="xs"
                                                                        variant="secondary"
                                                                        className="font-medium"
                                                                    >
                                                                        Jumlah
                                                                        Diambil
                                                                    </ThemedText>
                                                                </div>
                                                                <div className="col-span-3">
                                                                    <ThemedText
                                                                        size="xs"
                                                                        variant="secondary"
                                                                        className="font-medium"
                                                                    >
                                                                        Sisa
                                                                        Stok
                                                                    </ThemedText>
                                                                </div>
                                                            </div>
                                                            {item.warehouses.map(
                                                                (wh) => {
                                                                    const allocated =
                                                                        allocations[
                                                                            item
                                                                                .material_id
                                                                        ]?.[
                                                                            wh
                                                                                .warehouse_id
                                                                        ] || 0;
                                                                    const sisaStok =
                                                                        wh.qty -
                                                                        allocated;

                                                                    return (
                                                                        <div
                                                                            key={
                                                                                wh.warehouse_id
                                                                            }
                                                                            className="border-divider/50 grid grid-cols-12 items-center gap-2 border-t px-4 py-2"
                                                                        >
                                                                            <div className="col-span-4 pl-6">
                                                                                <ThemedText size="sm">
                                                                                    {
                                                                                        wh.name
                                                                                    }
                                                                                </ThemedText>
                                                                            </div>
                                                                            <div className="col-span-2">
                                                                                <ThemedText
                                                                                    size="sm"
                                                                                    variant="secondary"
                                                                                >
                                                                                    {formatThousands(
                                                                                        wh.qty,
                                                                                    )}{" "}
                                                                                    {
                                                                                        item.unit_name
                                                                                    }
                                                                                </ThemedText>
                                                                            </div>
                                                                            <div className="col-span-3">
                                                                                {(() => {
                                                                                    // Calculate remaining needed
                                                                                    const remaining =
                                                                                        item.required_qty -
                                                                                        item.totalAllocated;
                                                                                    // Max = min(warehouse stock, remaining + current allocation)
                                                                                    const smartMax =
                                                                                        Math.min(
                                                                                            wh.qty,
                                                                                            remaining +
                                                                                                allocated,
                                                                                        );
                                                                                    return (
                                                                                        <NumberInput
                                                                                            aria-label={`Jumlah dari ${wh.name}`}
                                                                                            size="sm"
                                                                                            variant="bordered"
                                                                                            min={
                                                                                                0
                                                                                            }
                                                                                            max={
                                                                                                smartMax
                                                                                            }
                                                                                            value={
                                                                                                allocated
                                                                                            }
                                                                                            onValueChange={(
                                                                                                val,
                                                                                            ) =>
                                                                                                updateAllocation(
                                                                                                    item.material_id,
                                                                                                    wh.warehouse_id,
                                                                                                    val,
                                                                                                    smartMax,
                                                                                                )
                                                                                            }
                                                                                            className="w-24"
                                                                                            onClick={(
                                                                                                e,
                                                                                            ) =>
                                                                                                e.stopPropagation()
                                                                                            }
                                                                                        />
                                                                                    );
                                                                                })()}
                                                                            </div>
                                                                            <div className="col-span-3">
                                                                                <ThemedText
                                                                                    size="sm"
                                                                                    className={
                                                                                        sisaStok <
                                                                                        0
                                                                                            ? "text-danger"
                                                                                            : "text-success"
                                                                                    }
                                                                                >
                                                                                    {formatThousands(
                                                                                        sisaStok,
                                                                                    )}{" "}
                                                                                    {
                                                                                        item.unit_name
                                                                                    }
                                                                                </ThemedText>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                },
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-default-400 py-8 text-center">
                                        Tidak ada data bahan baku
                                    </div>
                                )}
                            </div>
                        </ModalBody>

                        <ModalFooter>
                            <Button
                                variant="light"
                                onPress={onClose}
                                isDisabled={isSubmitting}
                                size="sm"
                            >
                                Batal
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleConfirm}
                                isLoading={isSubmitting}
                                size="sm"
                                isDisabled={
                                    !validationResults.isValid || isSubmitting
                                }
                                endContent={
                                    !isSubmitting && <LuChevronsRight />
                                }
                            >
                                Buat Produksi
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

export default ConfirmFormulaProductionModal;
